import {
  AllForemanId,
  DND_IDENTIFIER,
  EllipsisPlacement,
  TextWithEllipsis,
  UnassignedForemanId,
  useAppUser,
  useAvailableMilestoneTypesQuery,
  usePlanning,
} from '@bechtel/client-common';
import {
  belowBreakpoint,
  Box,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Text,
  Theme,
  useMediaQuery,
} from '@bechtel/gds';
import { MilestoneType } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { compact, find, get, isEqual, map } from 'lodash';
import React, { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { usePlanningPermissions } from '~/hooks';
import { BacklogFilter, defaultValues } from '../types';
import { BacklogHeaderContainerStyled, SearchCanvasButtonStyled, SearchFieldStyled, SeperatorStyled } from './styled';

interface BacklogHeaderProps {
  open: (e) => void;
  onChange: (e) => void;
  selectedMilestoneDraggableIds: string[];
  filterValues: BacklogFilter;
  unplanMilestones: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const BacklogHeader: React.FC<BacklogHeaderProps> = ({
  open,
  onChange,
  selectedMilestoneDraggableIds,
  filterValues,
  unplanMilestones,
  isCollapsed,
  onToggleCollapse,
}) => {
  const { canPlanMilestones } = usePlanningPermissions();
  const { selectedForeman } = usePlanning();

  const isUnplanMilestonesDisabled =
    !selectedMilestoneDraggableIds ||
    selectedMilestoneDraggableIds.length <= 0 ||
    (selectedMilestoneDraggableIds &&
      selectedMilestoneDraggableIds.some(
        (id) => id.includes(DND_IDENTIFIER.Unplanned) && !id.includes(DND_IDENTIFIER.Expired)
      ));
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const { user } = useAppUser();
  const { data } = useAvailableMilestoneTypesQuery(user?.project?.id, true);
  //TODO will replace Fontawseome icon star to original one TODO
  const milestoneList: MilestoneType[] = get(data, 'projectConfigById.availableMilestoneTypes', []);

  const {
    milestone,
    commodityTypes,
    materialStatus,
    commodityTypesCount,
    materialStatusCount,
    milestoneCount,
    expiredMilestoneFilter,
  } = filterValues;

  const milestoneNames = map(milestone, (id) => find(milestoneList, (m) => m.id === id)?.name);

  const isDefaultFilter =
    isEqual(defaultValues.commodityTypes, filterValues.commodityTypes) &&
    isEqual(defaultValues.milestone, filterValues.milestone) &&
    isEqual(defaultValues.materialStatus, filterValues.materialStatus) &&
    isEqual(defaultValues.expiredMilestoneFilter, filterValues.expiredMilestoneFilter);
  return (
    <BacklogHeaderContainerStyled
      isDisableHeader={selectedForeman?.id === AllForemanId || selectedForeman?.id === UnassignedForemanId}
      data-testid="backlog-header"
    >
      {isCollapsed ? (
        <Box display="flex" justifyContent="flex-end" alignItems="center" pr={{ sm: '200' }}>
          <Box
            onClick={onToggleCollapse}
            css="cursor: pointer; user-select: none; display: flex; align-items: center;"
            title="Click to expand"
          >
            <Icon icon="arrow-right" size={IconSize.MEDIUM} fillColor="#333" />
          </Box>
        </Box>
      ) : (
        <>
          <Box
            mb={{ sm: '500' }}
            alignItems="center"
            pr={{ sm: '200' }}
            css="display: flex; justify-content: space-between;"
          >
            <Text weight="600" size="400">
              <Trans>Backlog</Trans>
            </Text>
            <Box
              onClick={onToggleCollapse}
              css="cursor: pointer; user-select: none; display: flex; align-items: center;"
              title="Click to collapse"
            >
              <Icon icon="arrow-left" size={IconSize.MEDIUM} fillColor="#333" />
            </Box>
          </Box>
          <Box
            mb={{ sm: '400' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            data-testid="backlog-search"
          >
            <SearchFieldStyled placeholder={t`Search tag IDs`} type="search" onChange={onChange} />
            <Box ml={{ sm: '400' }}>
              <SearchCanvasButtonStyled
                isDefaultFilterChange={!isDefaultFilter}
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={open}
              >
                <Icon icon="sliders-v" size={IconSize.MEDIUM} fillColor={!isDefaultFilter ? 'white' : ''} />
              </SearchCanvasButtonStyled>
            </Box>
          </Box>
          <Box mt={{ sm: '200' }}>
            <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
              <Trans>Commodity:</Trans>&nbsp;
              <Text weight="400" title={compact(commodityTypes)?.join(' , ')}>
                {commodityTypes.length == 0 || commodityTypes.length == commodityTypesCount
                  ? t`All`
                  : compact(commodityTypes)?.join(' , ')}
              </Text>
            </TextWithEllipsis>
          </Box>
          <Box mt={{ sm: '200' }}>
            <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
              <Trans>Milestone:</Trans>&nbsp;
              <Text weight="400" title={compact(milestoneNames)?.join(' , ')}>
                {milestoneNames.length == 0 || milestoneNames.length == milestoneCount
                  ? t`All`
                  : compact(milestoneNames)?.join(' , ')}
              </Text>
            </TextWithEllipsis>
          </Box>
          <Box mt={{ sm: '200' }}>
            <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
              <Trans>Material Status:</Trans>&nbsp;
              <Text weight="400" title={compact(materialStatus)?.join(' , ')}>
                {materialStatus.length == 0 || materialStatus.length == materialStatusCount
                  ? t`All`
                  : compact(materialStatus)?.join(' , ')}
              </Text>
            </TextWithEllipsis>
          </Box>
          <Box mt={{ sm: '200' }}>
            <Text size="250" weight="600">
              <Trans>Expired Milestone:</Trans>&nbsp;
            </Text>
            <Text size="250" weight="400">
              {expiredMilestoneFilter ? t`Yes` : t`No`}
            </Text>
          </Box>
          <SeperatorStyled></SeperatorStyled>
        </>
      )}
    </BacklogHeaderContainerStyled>
  );
};
